import React, { useState } from "react";
import { Button, Dropdown, Table } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import { useMutation } from "@apollo/client";
import {
  UPDATE_DEFAULT_PRICING,
  UPDATE_USER_CUSTOM_PRICING,
} from "../../../api/graphql/users";
import _ from "lodash";
import { PriceSettingRow, ProfileLevels, StorePricing, Limits } from "./types";
import { useLocation } from "react-router-dom";
import useIsPhone from "../../../hooks/useIsPhone";
import PriceFields, { PriceGroup } from "../../PriceSettings/PriceFields";
import styles from "./PriceSettingsTab.module.scss";

interface PriceSettingsTabProps {
  userId?: number;
  priceSettings: PriceSettingRow;
  profileLevels: ProfileLevels | string;
  refetch: () => any;
  /** Shows the prices without the Edit menu. */
  readOnly?: boolean;
}

const initialPriceSettingsData: PriceSettingRow = {
  codeOfCountry: "9635",
  country: "BF",
  APP_STORE: {
    VISIT: 0,
    APP_OPENING: 0,
    DIRECT: 0,
    KEYWORDS: 0,
    KEYWORDS_PRO: null,
    RATINGS: 0,
    REVIEWS: 0,
  },
  GOOGLE_STORE: {
    VISIT: 0,
    APP_OPENING: 0,
    DIRECT: 0,
    KEYWORDS: 0,
    RATINGS: 0,
    REVIEWS: 0,
  },
  LIMITS: {
    apps: 0,
    keywords: 0,
  },
};

// The price columns of a store, for a phone's PriceFields; the table below
// spells out the same ones.
const STORE_GROUPS: {
  platform: "APP_STORE" | "GOOGLE_STORE";
  title: string;
  fields: { field: keyof StorePricing; title: string }[];
}[] = [
  {
    platform: "APP_STORE",
    title: "iOS",
    fields: [
      { field: "VISIT", title: "VISIT" },
      { field: "DIRECT", title: "Direct" },
      { field: "KEYWORDS", title: "Keyword" },
      { field: "KEYWORDS_PRO", title: "Keyword PRO" },
      { field: "RATINGS", title: "Ratings" },
      { field: "REVIEWS", title: "Reviews" },
    ],
  },
  {
    platform: "GOOGLE_STORE",
    title: "Android",
    fields: [
      { field: "VISIT", title: "VISIT" },
      { field: "DIRECT", title: "Direct" },
      { field: "KEYWORDS", title: "Keyword" },
      { field: "RATINGS", title: "Ratings" },
      { field: "REVIEWS", title: "Reviews" },
    ],
  },
];

const sanitizeStorePricing = (store: StorePricing): StorePricing =>
  (_.omit(store, ["__typename"]) as StorePricing) ?? store;

const sanitizeLimits = (limits?: Limits): Limits => {
  if (!limits) return { apps: 0, keywords: 0 };
  return (_.omit(limits, ["__typename"]) as Limits) ?? limits;
};

const sanitizePriceSettings = (settings: PriceSettingRow): PriceSettingRow => {
  const base = (_.omit(settings, ["__typename"]) ??
    {}) as PriceSettingRow;
  return {
    ...base,
    APP_STORE: sanitizeStorePricing(settings.APP_STORE),
    GOOGLE_STORE: sanitizeStorePricing(settings.GOOGLE_STORE),
    LIMITS: sanitizeLimits(settings.LIMITS),
  };
};

export const PriceSettingsTab: React.FC<PriceSettingsTabProps> = ({
  userId,
  priceSettings,
  profileLevels,
  refetch,
  readOnly = false,
}) => {
  const [priceSettingsData, setPriceSettingsData] = useState<PriceSettingRow>(
    sanitizePriceSettings(priceSettings || initialPriceSettingsData),
  );
  const [updateDefaultPricing] = useMutation(UPDATE_DEFAULT_PRICING);
  const [updateUserCustomPricing] = useMutation(UPDATE_USER_CUSTOM_PRICING);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const isPhone = useIsPhone();

  const handlePriceChange = (
    index: number,
    platform: "APP_STORE" | "GOOGLE_STORE",
    field: keyof StorePricing,
    value: string,
  ) => {
    const newData: PriceSettingRow = sanitizePriceSettings({
      codeOfCountry: priceSettingsData.codeOfCountry,
      country: priceSettingsData.country,
      GOOGLE_STORE: priceSettingsData.GOOGLE_STORE,
      APP_STORE: priceSettingsData.APP_STORE,
      LIMITS: priceSettingsData.LIMITS,
    });
    if (field === "KEYWORDS_PRO") {
      // An empty field clears the price and turns Keyword PRO off
      newData[platform].KEYWORDS_PRO = value === "" ? null : parseFloat(value);
    } else {
      newData[platform][field] = parseFloat(value);
    }
    setPriceSettingsData(newData);
  };

  const handleLimitChange = (
    index: number,
    field: keyof Limits,
    value: string,
  ) => {
    const newData: PriceSettingRow = sanitizePriceSettings({
      codeOfCountry: priceSettingsData.codeOfCountry,
      country: priceSettingsData.country,
      GOOGLE_STORE: priceSettingsData.GOOGLE_STORE,
      APP_STORE: priceSettingsData.APP_STORE,
      LIMITS: priceSettingsData.LIMITS,
    });
    if (!newData.LIMITS) {
      newData.LIMITS = { apps: 0, keywords: 0 };
    }
    newData.LIMITS[field] = parseFloat(value);
    setPriceSettingsData(newData);
  };
  const handleEdit = (index: number) => {
    setEditingIndex(index);
  };

  const getProfileLevelName = (level: ProfileLevels | string): string => {
    if (typeof level === "string") return level;
    const mapped = ProfileLevels[level];
    return typeof mapped === "string" ? mapped : String(level);
  };

  const handleSave = async (index: number) => {
    setEditingIndex(null);
    const newData = _.cloneDeepWith(
      { ...priceSettingsData },
      (value: string | number) => {
        if (typeof value === "string") {
          return parseFloat(value);
        }
      },
    );
    const sanitizedData = sanitizePriceSettings(newData);
    const profileLevelName = getProfileLevelName(profileLevels);

    if (!userId) {
      // If the profile level is not CUSTOM, update the default pricing
      await updateDefaultPricing({
        variables: {
          customPricing: {
            ...sanitizedData,
          },
          profileLevel: profileLevelName,
        },
      }).then(() => {
        refetch();
        // Update local state with the new data
        setPriceSettingsData(sanitizedData);
      });
    } else {
      await updateUserCustomPricing({
        variables: {
          customPricing: {
            APP_STORE: {
              ...sanitizedData.APP_STORE,
              DIRECT: sanitizedData.APP_STORE.DIRECT || 0,
            },
            GOOGLE_STORE: {
              ...sanitizedData.GOOGLE_STORE,
              DIRECT: sanitizedData.GOOGLE_STORE.DIRECT || 0,
            },
            LIMITS: sanitizedData.LIMITS || { apps: 0, keywords: 0 },
          },
          userId: userId,
        },
      }).then(() => {
        refetch();
        // Update local state with the new data
        setPriceSettingsData(sanitizedData);
      });
    }
  };

  // Delete was never wired up: the item only closed the hand-made menu,
  // which the Dropdown now does by itself.
  const handleDelete = (index: number) => {};

  const location = useLocation();

  // only display the button if the route is not /price-settings
  const banner = location.pathname !== "/price-settings" && (
    <div className={styles.banner}>Set general price settings</div>
  );

  if (isPhone) {
    // The table's one row, as its index 0.
    const groups: PriceGroup[] = [
      ...STORE_GROUPS.map(({ platform, title, fields }) => ({
        key: platform,
        title,
        fields: fields.map(({ field, title: fieldTitle }) => ({
          key: field,
          title: fieldTitle,
          // An empty Keyword PRO is off, as in the table.
          value:
            field === "KEYWORDS_PRO"
              ? (priceSettingsData[platform].KEYWORDS_PRO ?? "")
              : (priceSettingsData[platform][field] ?? 0),
          placeholder: field === "KEYWORDS_PRO" ? "—" : undefined,
          onChange: (value: string) =>
            handlePriceChange(0, platform, field, value),
        })),
      })),
      {
        key: "LIMITS",
        title: "Limits",
        fields: (["apps", "keywords"] as const).map((field) => ({
          key: field,
          title: field === "apps" ? "Apps" : "Keywords",
          value: priceSettingsData.LIMITS?.[field] || 0,
          onChange: (value: string) => handleLimitChange(0, field, value),
        })),
      },
    ];

    return (
      <div className={styles.wrap}>
        {banner}
        <PriceFields
          groups={groups}
          editing={editingIndex === 0}
          readOnly={readOnly}
          onEdit={() => handleEdit(0)}
          onSave={() => handleSave(0)}
          // Back to the prices last loaded.
          onCancel={() => {
            setPriceSettingsData(
              sanitizePriceSettings(priceSettings || initialPriceSettingsData),
            );
            setEditingIndex(null);
          }}
        />
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      {banner}
      <Table
        bordered
        size="small"
        className={styles.table}
        // 13 price columns share the card width instead of scrolling.
        tableLayout="fixed"
        pagination={false}
        dataSource={[priceSettingsData]}
        rowKey={(record) => record?.codeOfCountry || "price-settings-row"}
        columns={[
          {
            title: "iOS",
            children: [
              {
                title: "VISIT",
                dataIndex: ["APP_STORE", "VISIT"],
                key: "ios_VISIT",
                render: (value: number, record: any, index: number) => (
                  <input
                    type="number"
                    className={styles.input}
                    value={value ?? 0}
                    onChange={(e) =>
                      handlePriceChange(
                        index,
                        "APP_STORE",
                        "VISIT",
                        e.target.value,
                      )
                    }
                    disabled={editingIndex !== index}
                  />
                ),
              },
              {
                title: "Direct",
                dataIndex: ["APP_STORE", "DIRECT"],
                key: "ios_direct",
                render: (value: number, record: any, index: number) => (
                  <input
                    type="number"
                    className={styles.input}
                    value={value ?? 0}
                    onChange={(e) =>
                      handlePriceChange(
                        index,
                        "APP_STORE",
                        "DIRECT",
                        e.target.value,
                      )
                    }
                    disabled={editingIndex !== index}
                  />
                ),
              },
              {
                title: "Keyword",
                dataIndex: ["APP_STORE", "KEYWORDS"],
                key: "ios_keyword",
                render: (value: number, record: any, index: number) => (
                  <input
                    type="number"
                    className={styles.input}
                    value={value ?? 0}
                    onChange={(e) =>
                      handlePriceChange(
                        index,
                        "APP_STORE",
                        "KEYWORDS",
                        e.target.value,
                      )
                    }
                    disabled={editingIndex !== index}
                  />
                ),
              },
              {
                // Keyword PRO is App Store only, Android has no such column
                title: "Keyword PRO",
                dataIndex: ["APP_STORE", "KEYWORDS_PRO"],
                key: "ios_keyword_pro",
                render: (value: number | null, record: any, index: number) => (
                  <input
                    type="number"
                    className={styles.input}
                    value={value ?? ""}
                    placeholder="—"
                    onChange={(e) =>
                      handlePriceChange(
                        index,
                        "APP_STORE",
                        "KEYWORDS_PRO",
                        e.target.value,
                      )
                    }
                    disabled={editingIndex !== index}
                  />
                ),
              },
              {
                title: "Ratings",
                dataIndex: ["APP_STORE", "RATINGS"],
                key: "ios_ratings",
                render: (value: number, record: any, index: number) => (
                  <input
                    type="number"
                    className={styles.input}
                    value={value ?? 0}
                    onChange={(e) =>
                      handlePriceChange(
                        index,
                        "APP_STORE",
                        "RATINGS",
                        e.target.value,
                      )
                    }
                    disabled={editingIndex !== index}
                  />
                ),
              },
              {
                title: "Reviews",
                dataIndex: ["APP_STORE", "REVIEWS"],
                key: "ios_reviews",
                render: (value: number, record: any, index: number) => (
                  <input
                    type="number"
                    className={styles.input}
                    value={value ?? 0}
                    onChange={(e) =>
                      handlePriceChange(
                        index,
                        "APP_STORE",
                        "REVIEWS",
                        e.target.value,
                      )
                    }
                    disabled={editingIndex !== index}
                  />
                ),
              },
            ],
            align: "left",
          },
          {
            title: "Android",
            children: [
              {
                title: "VISIT",
                dataIndex: ["GOOGLE_STORE", "VISIT"],
                key: "android_VISIT",
                render: (value: number, record: any, index: number) => (
                  <input
                    type="number"
                    className={styles.input}
                    value={value ?? 0}
                    onChange={(e) =>
                      handlePriceChange(
                        index,
                        "GOOGLE_STORE",
                        "VISIT",
                        e.target.value,
                      )
                    }
                    disabled={editingIndex !== index}
                  />
                ),
              },
              {
                title: "Direct",
                dataIndex: ["GOOGLE_STORE", "DIRECT"],
                key: "android_direct",
                render: (value: number, record: any, index: number) => (
                  <input
                    type="number"
                    className={styles.input}
                    value={value ?? 0}
                    onChange={(e) =>
                      handlePriceChange(
                        index,
                        "GOOGLE_STORE",
                        "DIRECT",
                        e.target.value,
                      )
                    }
                    disabled={editingIndex !== index}
                  />
                ),
              },
              {
                title: "Keyword",
                dataIndex: ["GOOGLE_STORE", "KEYWORDS"],
                key: "android_keyword",
                render: (value: number, record: any, index: number) => (
                  <input
                    type="number"
                    className={styles.input}
                    value={value ?? 0}
                    onChange={(e) =>
                      handlePriceChange(
                        index,
                        "GOOGLE_STORE",
                        "KEYWORDS",
                        e.target.value,
                      )
                    }
                    disabled={editingIndex !== index}
                  />
                ),
              },
              {
                title: "Ratings",
                dataIndex: ["GOOGLE_STORE", "RATINGS"],
                key: "android_ratings",
                render: (value: number, record: any, index: number) => (
                  <input
                    type="number"
                    className={styles.input}
                    value={value ?? 0}
                    onChange={(e) =>
                      handlePriceChange(
                        index,
                        "GOOGLE_STORE",
                        "RATINGS",
                        e.target.value,
                      )
                    }
                    disabled={editingIndex !== index}
                  />
                ),
              },
              {
                title: "Reviews",
                dataIndex: ["GOOGLE_STORE", "REVIEWS"],
                key: "android_reviews",
                render: (value: number, record: any, index: number) => (
                  <input
                    type="number"
                    className={styles.input}
                    value={value ?? 0}
                    onChange={(e) =>
                      handlePriceChange(
                        index,
                        "GOOGLE_STORE",
                        "REVIEWS",
                        e.target.value,
                      )
                    }
                    disabled={editingIndex !== index}
                  />
                ),
              },
            ],
            align: "left",
          },
          {
            title: "Limits",
            children: [
              {
                title: "Apps",
                dataIndex: ["LIMITS", "apps"],
                key: "limits_apps",
                render: (value: number, record: any, index: number) => (
                  <input
                    type="number"
                    className={styles.input}
                    value={value || 0}
                    onChange={(e) =>
                      handleLimitChange(
                        index,
                        "apps",
                        e.target.value,
                      )
                    }
                    disabled={editingIndex !== index}
                  />
                ),
              },
              {
                title: "Keywords",
                dataIndex: ["LIMITS", "keywords"],
                key: "limits_keywords",
                render: (value: number, record: any, index: number) => (
                  <input
                    type="number"
                    className={styles.input}
                    value={value || 0}
                    onChange={(e) =>
                      handleLimitChange(
                        index,
                        "keywords",
                        e.target.value,
                      )
                    }
                    disabled={editingIndex !== index}
                  />
                ),
              },
            ],
            align: "left",
          },
          // Without this menu there is no way into edit mode.
          ...(readOnly
            ? []
            : [
                {
                  title: "",
                  key: "action",
                  width: 64, // fits the Save button in edit mode
                  render: (_: any, record: any, index: number) =>
                    editingIndex === index ? (
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => handleSave(index)}
                      >
                        Save
                      </Button>
                    ) : (
                      // antd Dropdown renders in a portal: the table scrolls
                      // horizontally and would clip a menu drawn inside it.
                      <Dropdown
                        trigger={["click"]}
                        placement="bottomRight"
                        menu={{
                          items: [
                            {
                              key: "edit",
                              label: "Edit",
                              onClick: () => handleEdit(index),
                            },
                            {
                              key: "delete",
                              label: "Delete",
                              onClick: () => handleDelete(index),
                            },
                          ],
                        }}
                      >
                        <button className={styles.actionButton} aria-label="Actions">
                          <MoreOutlined />
                        </button>
                      </Dropdown>
                    ),
                },
              ]),
        ]}
      />
    </div>
  );
};

export default PriceSettingsTab;
